import type { GameDefinition, Evidence, MusicMood } from '../types/game';
import type { GameEngine, DialogueView } from '../engine/GameEngine';
import type { EngineEvent } from '../engine/events';
import { PhaserView } from './PhaserView';
import { ExplorationView } from './ExplorationView';
import { WorldView } from './world3d/WorldView';
import { BattleView } from './BattleView';
import { MinigameView } from './MinigameView';
import { ContentPanel } from './ContentPanel';
import { VideoView, type VideoSpec } from './VideoView';
import { SoundManager } from './sound';
import { ProceduralAudio, type AmbientProfile } from './procedural-audio';
import { MusicDirector } from '../audio/MusicDirector';
import { SfxBank } from '../audio/SfxBank';
import { el, clear, button, on, type El } from './dom';

type DrawerTab = 'objectives' | 'inventory' | 'diary' | 'map' | 'puntaje';

export interface AdventureUIOptions {
  engine: GameEngine;
  assetBaseUrl?: string;
  reducedMotion?: boolean;
  /** Notifica al anfitrión cuando el jugador pulsa «Salir» (opcional). */
  onExit?: () => void;
}

/**
 * Capa de interfaz de una partida en curso.
 *
 * Construye el DOM (barra superior, escenario, diálogo, cajones, modales) y se
 * suscribe a los eventos del motor para repintar. No contiene lógica de juego:
 * todo lo decide el `GameEngine` a partir del JSON. Crea y gestiona su propia
 * vista Phaser.
 */
export class AdventureUI {
  private readonly root: El<'div'>;
  private readonly disposers: Array<() => void> = [];
  private readonly sound: SoundManager;
  private readonly procedural: ProceduralAudio;
  private readonly music: MusicDirector;
  private readonly sfx: SfxBank;
  private sceneMood: MusicMood = 'aventura';
  private battleActive = false;
  private finished = false;
  private readonly phaser?: PhaserView;
  private readonly view?: ExplorationView | WorldView;
  private battle?: BattleView;
  private minigame?: MinigameView;
  private content?: ContentPanel;
  private video?: VideoView;
  private readonly isExplorationMode: boolean;
  private readonly hasScoreboard: boolean;

  // Referencias persistentes
  private objectiveLineEl!: El<'div'>;
  private objectiveCountsEl!: El<'div'>;
  private progressFill!: El<'div'>;
  private lastPct = -1;
  private sceneDescEl!: El<'div'>;
  private hotspotsEl!: El<'div'>;
  private dialogueEl!: El<'div'>;
  private toastsEl!: El<'div'>;
  private drawerEl!: El<'div'>;
  private drawerBody!: El<'div'>;
  private modalHost!: El<'div'>;
  private canvasHolder!: El<'div'>;
  private worldScoreEl!: El<'div'>;
  private worldScore = 0;

  // Estado de UI (no persistido en la partida)
  private drawerOpen = false;
  private drawerTab: DrawerTab = 'objectives';
  private revealedHints: Record<string, number> = {};
  private lastPhaserSig = '';
  private selection = { hypothesis: '', evidence: [] as string[], measures: [] as string[] };

  // Typewriter state
  private typewriterSig = '';
  private typewriterTimer: number | null = null;
  private typewriterDone = false;
  private dialogueActive = false;

  private musicOn: boolean = localStorage.getItem('ae-music') !== 'off';

  constructor(private readonly host: HTMLElement, private readonly opts: AdventureUIOptions) {
    const def = opts.engine.game;
    this.sound = new SoundManager(opts.assetBaseUrl ?? '');
    this.procedural = new ProceduralAudio();
    this.music = new MusicDirector(this.procedural);
    this.sfx = new SfxBank(this.procedural);
    this.isExplorationMode = def.mode === 'exploration';
    this.hasScoreboard = Object.values(def.minigames ?? {}).some((m) => (m.puntos ?? 0) > 0);
    this.root = el('div', { className: 'ae-root' });
    this.host.append(this.root);
    this.buildShell(def);

    this.procedural.setReducedMotion(opts.reducedMotion ?? false);
    this.music.setReducedMotion(opts.reducedMotion ?? false);
    this.sfx.setReducedMotion(opts.reducedMotion ?? false);
    this.music.setEnabled(this.musicOn);

    // Mientras el mundo 3D esté en revisión activa, sale SIEMPRE por defecto
    // para cualquier aventura de exploración. El antiguo ExplorationView 2D
    // queda desactivado temporalmente: si el OA no trae `world`, sintetizamos
    // un jardín con marcadores sobre las interacciones del propio OA.
    // Se puede volver al modo 2D pasando `?3d=0` (o `?view=2d`) en la URL.
    if (this.isExplorationMode && def.exploration) {
      const startMap = def.maps?.[def.exploration.startMap];
      const callbacks = {
        onInteract: (id: string) => this.opts.engine.interact(id),
        onBlocked: (hint: string) => this.toast(hint, 'hint'),
        onSequenceSfx: (sound: string) => this.sfx.play(sound),
        onMapEnter: (mapId: string) => this.onMapEnter(mapId),
        onCheckCondition: (condition: unknown) => this.opts.engine.evaluateCondition(condition as import('../types/game').Condition),
        onInteractionAvailable: (id: string) => this.opts.engine.isInteractionAvailable(id),
        onInteractionEnabled: (id: string) => this.opts.engine.isInteractionEnabled(id),
        onScore: (points: number, reason: string) => this.addWorldScore(points, reason)
      };
      const startSpawn = def.exploration.startSpawn;
      const params = new URLSearchParams(window.location.search);
      const want2d = params.get('3d') === '0' || params.get('view') === '2d';
      const use3d = !want2d;
      if (!startMap) {
        // Sin mapa inicial no se puede sintetizar nada en 3D; cae al Phaser
        // para no quedarnos con la pantalla en negro.
        this.phaser = new PhaserView(
          this.canvasHolder,
          def,
          {
            onInteract: (id) => this.opts.engine.interact(id),
            onBlocked: (hint) => this.toast(hint, 'hint'),
            onSequenceSfx: (sound) => this.sfx.play(sound)
          },
          opts.assetBaseUrl ?? ''
        );
        this.phaser.setReducedMotion(opts.reducedMotion ?? false);
        this.phaser.create();
      } else if (use3d) {
        if (!startMap.world) {
          // 3D por defecto: sintetiza jardín cuando el OA no trae `world`.
          this.synthesizeDefaultWorld(startMap);
        }
        const world = new WorldView(this.canvasHolder, def, callbacks, opts.assetBaseUrl ?? '');
        world.setReducedMotion(opts.reducedMotion ?? false);
        world.create();
        world.loadMap(def.exploration.startMap, startSpawn);
        this.view = world;
      } else {
        // Modo 2D clásico, sólo opt-in mientras conviven ambos.
        const exploration = new ExplorationView(this.canvasHolder, def, callbacks, opts.assetBaseUrl ?? '');
        exploration.setReducedMotion(opts.reducedMotion ?? false);
        exploration.create();
        exploration.loadMap(def.exploration.startMap, startSpawn);
        this.view = exploration;
      }
    } else {
      this.phaser = new PhaserView(
        this.canvasHolder,
        def,
        {
          onInteract: (id) => this.opts.engine.interact(id),
          onBlocked: (hint) => this.toast(hint, 'hint'),
          onSequenceSfx: (sound) => this.sfx.play(sound)
        },
        opts.assetBaseUrl ?? ''
      );
      this.phaser.setReducedMotion(opts.reducedMotion ?? false);
      this.phaser.create();
    }

    this.subscribe();
    this.render();
    if (this.opts.engine.isFinished() && this.opts.engine.getEnding()) {
      this.finished = true;
      this.music.setMood('victoria');
      this.showEnding(this.opts.engine.getState().ending!);
    }
  }

  /* --------------------------- construcción --------------------------- */

  private buildShell(def: GameDefinition): void {
    const titleEl = el('h1', { className: 'ae-topbar-title', textContent: def.metadata.title });
    this.objectiveLineEl = el('div');
    this.objectiveCountsEl = el('div');
    this.progressFill = el('div', { className: 'ae-progress-fill' });
    const objective = el('div', { className: 'ae-objective' }, [
      this.objectiveLineEl,
      this.objectiveCountsEl,
      el('div', { className: 'ae-progress-track' }, [this.progressFill])
    ]);

    const controls = el('div', { className: 'ae-controls' });
    controls.append(
      button('Pista', () => this.showNextHint(), { title: 'Mostrar una pista gradual' }),
      button('Objetivos', () => this.toggleDrawer('objectives')),
      button('Inventario', () => this.toggleDrawer('inventory')),
      button('Diario', () => this.toggleDrawer('diary')),
      button('Mapa', () => this.toggleDrawer('map'))
    );
    if (this.hasScoreboard) {
      controls.append(button('Marcador', () => this.toggleDrawer('puntaje')));
    }
    if (Object.keys(def.assets.audio ?? {}).length > 0) {
      const soundBtn = button('🔇 Sonido', () => this.toggleSound(soundBtn));
      controls.append(soundBtn);
    }
    const musicBtn = button(this.musicOn ? '🎵 Música' : '🔇 Música', () => this.toggleMusic(musicBtn), {
      title: 'Activar o silenciar la música de fondo',
      ariaLabel: 'Música de fondo'
    });
    musicBtn.setAttribute('aria-pressed', String(this.musicOn));
    controls.append(musicBtn);
    controls.append(
      button('⛶', () => this.toggleFullscreen(), { className: 'ae-btn icon', title: 'Pantalla completa', ariaLabel: 'Pantalla completa' }),
      button('↺', () => this.confirmRestart(), { className: 'ae-btn icon danger', title: 'Reiniciar partida', ariaLabel: 'Reiniciar partida' })
    );
    if (this.opts.onExit) {
      controls.append(button('Salir', () => this.opts.onExit?.(), { title: 'Volver al selector de aventuras' }));
    }

    const topbar = el('header', { className: 'ae-topbar' }, [titleEl, objective, controls]);

    this.canvasHolder = el('div', { className: 'ae-canvas' });
    this.sceneDescEl = el('div', { className: 'ae-scene-desc' });
    this.toastsEl = el('div', { className: 'ae-toasts', attrs: { role: 'status', 'aria-live': 'polite' } });
    this.dialogueEl = el('div', { className: 'ae-dialogue', attrs: { style: 'display:none' } });
    this.hotspotsEl = el('div', { className: 'ae-hotspots', attrs: { 'aria-label': 'Interacciones disponibles' } });
    this.worldScoreEl = el('div', { className: 'ae-worldscore', attrs: { style: 'display:none', 'aria-live': 'polite' } });

    const stage = el('div', { className: 'ae-stage' }, [
      this.canvasHolder,
      this.sceneDescEl,
      this.toastsEl,
      this.dialogueEl,
      this.hotspotsEl,
      this.worldScoreEl
    ]);

    this.drawerBody = el('div', { className: 'ae-drawer-body' });
    const drawerHead = el('div', { className: 'ae-drawer-head' }, [
      el('strong', { textContent: 'Panel' }),
      button('✕', () => this.closeDrawer(), { className: 'ae-btn icon', ariaLabel: 'Cerrar panel' })
    ]);
    this.drawerEl = el('div', { className: 'ae-drawer', attrs: { role: 'complementary' } }, [drawerHead, this.drawerBody]);

    const main = el('main', { className: 'ae-main' }, [stage, this.drawerEl]);
    this.modalHost = el('div', { attrs: { style: 'position:absolute;inset:0;pointer-events:none;z-index:50' } });
    this.root.append(topbar, main, this.modalHost);
  }

  /* --------------------------- suscripción --------------------------- */

  /**
   * Inyecta un mundo 3D mínimo en el `startMap` cuando el OA sólo trae
   * layout 2D. Pensado para `?3d=1` en previsualización: coloca las
   * interacciones del propio OA como marcadores sobre un jardín sintético,
   * y lo decora con vegetación, mobiliario y un camino diseñado, para que
   * el lugar no se sienta vacío cuando un OA trae pocos bloques.
   * Devuelve el ancho del cuadrado generado por si se quiere sincronizar.
   */
  private synthesizeDefaultWorld(startMap: NonNullable<NonNullable<GameDefinition['maps']>[string]>): number {
    const interCount = Math.max(4, Object.keys(startMap.interactions ?? {}).length);
    const side = Math.min(40, 16 + Math.round(interCount * 1.5));
    // Coordenadas aproximadas (en tiles del jardin) en las que el renderer
    // 3D plantará los marcadores amarillos/verdes; las respetamos como zonas
    // prohibidas para no tapar el punto con un banco ni con un árbol.
    const markerTiles: Array<{ x: number; z: number }> = [];
    for (const inter of Object.values(startMap.interactions ?? {})) {
      const p = inter.position;
      if (!p) continue;
      markerTiles.push({ x: p.x * side, z: p.y * side });
    }
    const props = AdventureUI.decorateSynthGarden(side, markerTiles, startMap.id);
    startMap.world = {
      kind: 'jardin',
      tilesWide: side,
      tilesDeep: side,
      ground: 'grass',
      props,
      // Sendero circular en torno a la fuente central: da pie al jugador a
      // recorrerlo y reparte las paradas de ejercicio por el anillo.
      path: Array.from({ length: 16 }, (_, i) => {
        const a = (i / 16) * Math.PI * 2;
        const r = Math.max(4, side * 0.32);
        return { x: side / 2 + Math.cos(a) * r, z: side / 2 + Math.sin(a) * r };
      })
    } as NonNullable<NonNullable<GameDefinition['maps']>[string]>['world'];
    return side;
  }

  /**
   * Decora el jardin sintético con un diseño estable por semilla. Coloca
   * la fuente en el centro, árboles y farolas en el perímetro, bancos a
   * ambos lados del camino circular, y flores + arbustos dispersos entre
   * esos anclajes. Evita los tiles donde el renderer plantará marcadores.
   */
  private static decorateSynthGarden(
    side: number,
    markerTiles: Array<{ x: number; z: number }>,
    mapId: string
  ): import('../types/game').PlacedProp[] {
    const out: import('../types/game').PlacedProp[] = [];
    const safe = (x: number, z: number, minDist = 2.4): boolean => {
      // Dentro de los límites.
      if (x < 1.5 || x > side - 1.5 || z < 1.5 || z > side - 1.5) return false;
      // Lejos de cada marcador; así el mobiliario no tapa el punto brillante.
      for (const m of markerTiles) {
        if (Math.hypot(m.x - x, m.z - z) < minDist) return false;
      }
      return true;
    };
    const rand = AdventureUI.seededFloat(mapId);
    const next = (): number => rand();

    // 1) Landmark: fuente en el centro. Ancla visual y motivo del Sendero.
    out.push({ role: 'fountain', x: side / 2, z: side / 2, yaw: 0, scale: 1.4, collider: true });

    // 2) Sendero (4 bancos a lo largo del anillo, alternando lados).
    const pathRadius = Math.max(4, side * 0.32);
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2 + Math.PI / 4;
      const bx = side / 2 + Math.cos(a) * (pathRadius - 1.5);
      const bz = side / 2 + Math.sin(a) * (pathRadius - 1.5);
      const yaw = (a * 180) / Math.PI + 90;
      if (safe(bx, bz, 2.0)) out.push({ role: 'bench', x: bx, z: bz, yaw, scale: 1, collider: true });
    }

    // 3) Perímetro: árboles en las 4 esquinas + un par de setos a media pared.
    const cornerOffsets: Array<[number, number]> = [
      [2.2, 2.2],
      [side - 2.2, 2.2],
      [side - 2.2, side - 2.2],
      [2.2, side - 2.2]
    ];
    for (const [x, z] of cornerOffsets) out.push({ role: 'tree', x, z, yaw: next() * 360, scale: 1.1 + next() * 0.3, collider: true });
    // Setos a mitad de pared (entrada y salida visual del jardin).
    for (let i = 0; i < 4; i++) {
      const a = (i / 4) * Math.PI * 2;
      const lx = side / 2 + Math.cos(a) * (side * 0.42);
      const lz = side / 2 + Math.sin(a) * (side * 0.42);
      if (safe(lx, lz, 2.6)) out.push({ role: 'streetlamp', x: lx, z: lz, yaw: (a * 180) / Math.PI + 90, scale: 1, collider: true });
    }

    // 4) Vegetación dispersa entre los anclajes. Hasta 14 piezas (flores,
    //    arbustos, rocas) aceptando las que pasen el filtro `safe`.
    const candidates: Array<{ role: string; minDist: number }> = [
      { role: 'flower', minDist: 1.4 },
      { role: 'bush', minDist: 1.8 },
      { role: 'rock', minDist: 1.6 },
      { role: 'plant', minDist: 1.6 },
      { role: 'crate', minDist: 2.0 }
    ];
    let placed = 0;
    let attempts = 0;
    while (placed < 14 && attempts < 200) {
      attempts++;
      const c = candidates[Math.floor(next() * candidates.length)];
      const a = next() * Math.PI * 2;
      const r = 4 + next() * (side * 0.38);
      const x = side / 2 + Math.cos(a) * r;
      const z = side / 2 + Math.sin(a) * r;
      if (!safe(x, z, c.minDist)) continue;
      out.push({ role: c.role, x, z, yaw: next() * 360, scale: 0.8 + next() * 0.4 });
      placed++;
    }

    // 5) Cartel temático (lee "Aprende…") cerca del spawn del jugador;
    //    pone título humano al entorno y guía la entrada al jardin.
    const signX = Math.min(side - 2.5, 4);
    const signZ = side - 2.5;
    if (safe(signX, signZ, 1.6)) out.push({ role: 'sign', x: signX, z: signZ, yaw: 180, scale: 1, collider: true });

    return out;
  }

  /** LCG sembrado por el id de la sala: layout estable por OA. */
  private static seededFloat(seed: string): () => number {
    let h = 2166136261;
    for (let i = 0; i < seed.length; i++) {
      h ^= seed.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    let s = (h >>> 0) || 1;
    return () => {
      s = (Math.imul(s, 1664525) + 1013904223) >>> 0;
      return (s & 0xfffffff) / 0xfffffff;
    };
  }

  private subscribe(): void {
    this.disposers.push(this.opts.engine.on((e) => this.handleEvent(e)));
    this.disposers.push(on(document, 'keydown', (ev) => this.onKey(ev as KeyboardEvent)));
    this.disposers.push(on(this.root, 'pointerdown', () => {
      this.sound.unlock();
    }));
  }

  private handleEvent(e: EngineEvent): void {
    switch (e.type) {
      case 'message':
        this.toast(e.text, e.tone);
        break;
      case 'conclusionOpened':
        this.openConclusion();
        break;
      case 'gameCompleted':
        this.render();
        this.finished = true;
        this.battleActive = false;
        this.music.setMood('victoria');
        this.sfx.play('aplauso');
        this.showEnding(e.ending);
        return;
      case 'evidenceCollected':
        this.toast('Nueva evidencia registrada en tu diario.', 'success');
        this.sfx.play('moneda');
        this.phaser?.playEvidenceBurst();
        break;
      case 'objectiveCompleted': {
        const obj = this.opts.engine.game.objectives[e.objective];
        this.toast(obj ? `Objetivo completado: ${obj.title}` : 'Objetivo completado', 'success');
        this.sfx.play('nivel');
        this.phaser?.playScreenShake();
        break;
      }
      case 'dialogueStarted':
        this.closeDrawer();
        this.sfx.play('pop');
        if (this.isExplorationMode) {
          this.view?.setDialogueFraming(true);
          // Solo bloquea el movimiento si de verdad hay un cuadro de diálogo que
          // mostrar; un diálogo sin nodo visible no debe dejar al jugador parado.
          if (this.opts.engine.getDialogueView()) this.view?.setMoveEnabled(false);
        }
        break;
      case 'dialogueEnded':
        if (this.isExplorationMode) {
          this.view?.setMoveEnabled(true);
          this.view?.setDialogueFraming(false);
        }
        break;
      case 'sceneChanged':
        this.sfx.play('whoosh');
        break;
      case 'cameraRequested':
        if (!this.isExplorationMode) {
          if (e.action === 'zoom') {
            this.phaser?.cameraZoom(e.target, e.scale, e.duration, e.hold);
          } else {
            this.phaser?.cameraPan(e.to!, e.duration, e.hold);
          }
        }
        break;
      case 'objectStateChanged': {
        if (e.sfx) this.sfx.play(e.sfx);
        if (this.isExplorationMode) {
          this.view?.setObjectState(e.object, e.state);
        } else {
          const sceneDef = this.opts.engine.game.scenes[this.opts.engine.getState().currentScene];
          if (sceneDef) this.phaser?.setObjectState(e.object, e.state, sceneDef);
        }
        break;
      }
      case 'videoRequested': {
        this.openVideo({
          video: e.video,
          titulo: e.titulo,
          descripcion: e.descripcion,
          modo: e.modo,
          objeto: e.objeto,
          ancho: e.ancho,
          poster: e.poster
        });
        break;
      }
      case 'sequenceStarted': {
        if (!this.isExplorationMode) {
          const sceneDef = this.opts.engine.game.scenes[this.opts.engine.getState().currentScene];
          const seqDef = sceneDef?.sequences?.[e.sequence];
          if (seqDef) {
            this.phaser?.playSequence(seqDef, () => this.opts.engine.notifySequenceEnded());
          } else {
            this.opts.engine.notifySequenceEnded();
          }
        } else {
          this.opts.engine.notifySequenceEnded();
        }
        break;
      }
      case 'encounterStarted': {
        if (this.isExplorationMode) this.view?.setMoveEnabled(false);
        this.modalHost.style.pointerEvents = 'auto';
        this.battleActive = true;
        this.music.setMood('batalla');
        this.battle = new BattleView(this.modalHost, this.opts.engine, {
          onSfx: (name) => this.sfx.play(name),
          onVictory: () => {
            this.battle?.close();
            this.battle = undefined;
            this.modalHost.style.pointerEvents = 'none';
            if (this.isExplorationMode) this.view?.setMoveEnabled(true);
            this.battleActive = false;
            this.music.setMood(this.sceneMood);
            this.sfx.play('exito');
            this.opts.engine.executeAction({ type: 'showMessage', text: '¡Guardián restaurado!', tone: 'success' });
          }
        });
        this.battle.startBattle(e.encounter);
        break;
      }
      case 'contentStarted': {
        const spec = this.opts.engine.game.contents?.[e.content];
        if (!spec) break;
        if (this.isExplorationMode) this.view?.setMoveEnabled(false);
        this.content?.destroy();
        this.content = new ContentPanel(
          this.modalHost,
          spec,
          this.opts.engine.game,
          this.opts.assetBaseUrl ?? '',
          {
            onSfx: (name) => this.sfx.play(name),
            onClose: () => {
              this.content = undefined;
              if (this.isExplorationMode && !this.battleActive && !this.minigame && !this.video) {
                this.view?.setMoveEnabled(true);
              }
              this.opts.engine.resolveContent(e.content);
            }
          }
        );
        this.content.open();
        break;
      }
      case 'minigameStarted': {
        const spec = this.opts.engine.game.minigames?.[e.minigame];
        if (!spec) break;
        if (this.isExplorationMode) this.view?.setMoveEnabled(false);
        this.minigame?.destroy();
        this.minigame = new MinigameView(this.modalHost, spec, {
          onSfx: (name) => this.sfx.play(name),
          onClose: (outcome) => {
            this.minigame = undefined;
            if (this.isExplorationMode && !this.battleActive) this.view?.setMoveEnabled(true);
            this.opts.engine.resolveMinigame(e.minigame, outcome);
          }
        });
        this.minigame.open();
        break;
      }
      case 'encounterEnded': {
        this.battle?.close();
        this.battle = undefined;
        if (this.isExplorationMode) this.view?.setMoveEnabled(true);
        this.battleActive = false;
        this.music.setMood(this.sceneMood);
        break;
      }
      default:
        break;
    }
    this.render();
  }

  /** Resuelve la URL de un asset de imagen opcional. */
  private imageSrc(key: string | undefined): string | undefined {
    if (!key) return undefined;
    const img = this.opts.engine.game.assets.images?.[key];
    if (!img) return undefined;
    return img.src.startsWith('data:') ? img.src : (this.opts.assetBaseUrl ?? '') + img.src;
  }

  /**
   * Abre el reproductor pedido por una acción `playVideo`.
   *
   * La TV anclada sólo tiene sentido en modo exploración, donde hay objetos con
   * posición en un mapa; en cualquier otro caso se degrada a modal centrado para
   * que el vídeo nunca se pierda.
   */
  private openVideo(spec: VideoSpec): void {
    const tv = spec.modo === 'tv' && this.isExplorationMode && !!spec.objeto;
    this.video?.destroy();

    const musicaActiva = this.musicOn;
    this.music.setEnabled(false);
    if (this.isExplorationMode) this.view?.setMoveEnabled(false);
    if (!tv) this.modalHost.style.pointerEvents = 'auto';

    this.video = new VideoView(tv ? this.canvasHolder : this.modalHost, spec, {
      posterSrc: this.imageSrc(spec.poster),
      anchor: tv
        ? (objeto, ancho) => this.view?.getObjectScreenRect(objeto, ancho) ?? null
        : undefined,
      onClose: () => {
        this.video = undefined;
        this.music.setEnabled(musicaActiva);
        if (!tv) this.modalHost.style.pointerEvents = 'none';
        if (this.isExplorationMode && !this.battleActive && !this.minigame) {
          this.view?.setMoveEnabled(true);
        }
      }
    });
    this.video.open();
  }

  private onKey(ev: KeyboardEvent): void {
    if (ev.key === 'Escape' && this.drawerOpen && !this.modalHost.firstChild) {
      this.closeDrawer();
    }
    if ((ev.key === ' ' || ev.key === 'Enter') && !this.modalHost.firstChild) {
      const view = this.opts.engine.getDialogueView();
      if (view?.isContinue && ev.target === document.body) {
        ev.preventDefault();
        this.opts.engine.advanceDialogue();
      }
    }
  }

  /* --------------------------- renderizado --------------------------- */

  private render(): void {
    const engine = this.opts.engine;
    const scene = engine.getScene();
    const active = engine.getActiveObjective();
    const prog = engine.getProgress();

    clear(this.objectiveLineEl);
    this.objectiveLineEl.append(
      active
        ? el('span', {}, ['Objetivo: ', el('strong', { textContent: active.title })])
        : el('span', { textContent: 'Investigación completada' })
    );
    this.objectiveCountsEl.textContent =
      `Evidencias ${prog.evidence}/${prog.totalEvidence} · Escenarios ${prog.visitedScenes}/${prog.totalScenes} · Objetivos ${prog.objectives}/${prog.totalObjectives}`;
    // La barra refleja el progreso real: evidencias cuando las hay; si no
    // (aventuras de exploración por objetivos), el avance de los objetivos.
    const pct = prog.totalEvidence
      ? Math.round((prog.evidence / prog.totalEvidence) * 100)
      : prog.totalObjectives
        ? Math.round((prog.objectives / prog.totalObjectives) * 100)
        : 0;
    this.progressFill.style.width = `${pct}%`;
    if (pct > this.lastPct && this.lastPct >= 0) {
      this.progressFill.classList.remove('ae-bump');
      void this.progressFill.offsetWidth;
      this.progressFill.classList.add('ae-bump');
      window.setTimeout(() => this.progressFill.classList.remove('ae-bump'), 700);
    }
    this.lastPct = pct;

    const media = this.isExplorationMode
      ? engine.game.maps?.[engine.getState().currentScene]
      : scene;

    this.sceneDescEl.textContent = media?.description ?? '';
    this.sound.setAmbient(media?.ambient ? engine.game.assets.audio?.[media.ambient]?.src : undefined);

    // Ambiente: si el contenido no declara audio propio, se deriva de timeOfDay + ambiance
    if (!media?.ambient) {
      this.music.setAmbientProfile(this.getAmbientProfile(media));
    } else {
      this.music.stopAmbient();
    }
    this.applyMood(media);

    if (!this.isExplorationMode) {
      this.renderHotspots();
    }
    this.renderDialogue();

    const interactions = engine.getVisibleInteractions();
    const sig = `${engine.getState().currentScene}|${interactions.map((i) => i.id + (i.enabled ? '1' : '0')).join(',')}`;
    if (sig !== this.lastPhaserSig) {
      if (!this.isExplorationMode) {
        this.phaser?.setObjectStates(engine.getState().objectStates);
        this.phaser?.showScene(engine.getState().currentScene, interactions);
      }
      this.lastPhaserSig = sig;
    }

    if (this.drawerOpen) this.renderDrawer();
  }

  /**
   * Elige la pista de fondo del escenario. El contenido puede declararla con
   * `music`; si no, se deriva de la hora del día. Durante un combate o al
   * terminar la partida la pista la fijan esos momentos, no el escenario.
   */
  private applyMood(media: { music?: MusicMood; timeOfDay?: string } | undefined): void {
    this.sceneMood = media?.music ?? (media?.timeOfDay === 'night' ? 'misterio' : 'aventura');
    if (this.battleActive || this.finished) return;
    this.music.setMood(this.sceneMood);
  }

  /** Determina el perfil de audio procedural según timeOfDay + ambiance. */
  private getAmbientProfile(scene: { timeOfDay?: string; ambiance?: string } | undefined): AmbientProfile {
    if (!scene) return 'silent';
    const time = scene.timeOfDay;
    const ambiance = scene.ambiance;

    // Lluvia tiene prioridad
    if (ambiance === 'rain') return 'rain';
    if (ambiance === 'fireflies') return 'fireflies';
    if (ambiance === 'embers') return 'embers';

    // Perfiles por hora del día
    if (time === 'dusk') return 'dusk-dust';
    if (time === 'night') return 'night';
    if (time === 'dawn') return 'dawn-mist';
    if (time === 'day') return 'day';

    return 'silent';
  }

  private renderHotspots(): void {
    const engine = this.opts.engine;
    clear(this.hotspotsEl);
    const interactions = engine.getVisibleInteractions();
    const exits = engine.getVisibleExits();
    if (interactions.length === 0 && exits.length === 0) {
      this.hotspotsEl.append(el('span', { className: 'ae-hotspots-empty', textContent: 'No hay nada que examinar aquí ahora mismo.' }));
      return;
    }
    for (const inter of interactions) {
      const b = button('', () => engine.interact(inter.id), {
        className: `ae-hotspot ${inter.kind ?? 'hotspot'}`,
        disabled: !inter.enabled,
        title: inter.enabled ? inter.description ?? inter.label : inter.disabledHint ?? inter.label
      });
      b.append(el('span', { className: 'dot' }), el('span', { textContent: inter.label }));
      this.hotspotsEl.append(b);
    }
    for (const exit of exits) {
      const b = button('', () => engine.travelTo(exit.to), { className: 'ae-hotspot exit', title: `Ir a ${exit.label}` });
      b.append(el('span', { className: 'dot' }), el('span', { textContent: `→ ${exit.label}` }));
      this.hotspotsEl.append(b);
    }
  }

  private renderDialogue(): void {
    const view = this.opts.engine.getDialogueView();
    if (!view) {
      this.stopTypewriter();
      this.dialogueEl.style.display = 'none';
      if (this.dialogueActive) {
        this.dialogueActive = false;
        this.phaser?.hideLetterbox();
      }
      // Autocuración: si no hay cuadro de diálogo visible, el movimiento vuelve a
      // estar libre (salvo que otro modal lo esté bloqueando).
      if (this.isExplorationMode && !this.battleActive && !this.minigame && !this.video && !this.content) {
        this.view?.setMoveEnabled(true);
        this.view?.setDialogueFraming(false);
      }
      return;
    }

    if (!this.dialogueActive) {
      this.dialogueActive = true;
      if (!this.isExplorationMode) {
        this.phaser?.showLetterbox();
        this.phaser?.playDialogueZoom();
      }
    }

    const sig = `${view.speakerName}|${view.text}|${view.choices.map(c => c.id).join(',')}`;
    if (sig === this.typewriterSig && this.dialogueEl.style.display !== 'none') {
      this.updateDialogueActions();
      return;
    }

    this.stopTypewriter();
    this.typewriterSig = sig;
    this.typewriterDone = false;
    clear(this.dialogueEl);
    this.dialogueEl.style.display = 'flex';
    this.dialogueEl.append(...this.buildDialogueNode(view));
    this.dialogueEl.querySelector<HTMLElement>('button')?.focus();
    this.setupDialogue3DTilt();
    this.startTypewriter(view);
  }

  private updateDialogueActions(): void {
    const actionsEl = this.dialogueEl.querySelector<HTMLElement>('.ae-dialogue-actions');
    const choicesEl = this.dialogueEl.querySelector<HTMLElement>('.ae-choices');
    if (this.typewriterDone) {
      if (actionsEl) actionsEl.style.display = 'flex';
      if (choicesEl) choicesEl.style.display = 'flex';
    } else {
      if (actionsEl) actionsEl.style.display = 'none';
      if (choicesEl) choicesEl.style.display = 'none';
    }
  }

  private startTypewriter(view: DialogueView): void {
    const textEl = this.dialogueEl.querySelector<HTMLElement>('.ae-dialogue-text');
    const cursorEl = this.dialogueEl.querySelector<HTMLElement>('.ae-typewriter-cursor');
    if (!textEl) return;

    const fullText = view.text;
    let charIndex = 0;

    const skip = () => {
      if (this.typewriterDone) return;
      this.stopTypewriter();
      this.typewriterDone = true;
      textEl.textContent = fullText;
      this.updateDialogueActions();
    };

    this.dialogueEl.addEventListener('pointerdown', skip, { once: true });

    const hideActions = () => {
      const actionsEl = this.dialogueEl.querySelector<HTMLElement>('.ae-dialogue-actions');
      const choicesEl = this.dialogueEl.querySelector<HTMLElement>('.ae-choices');
      if (actionsEl) actionsEl.style.display = 'none';
      if (choicesEl) choicesEl.style.display = 'none';
    };
    hideActions();

    this.typewriterTimer = window.setInterval(() => {
      if (charIndex < fullText.length) {
        textEl.textContent = fullText.slice(0, charIndex + 1);
        charIndex++;
      } else {
        this.stopTypewriter();
        this.typewriterDone = true;
        if (cursorEl) cursorEl.remove();
        this.updateDialogueActions();
      }
    }, 30);
  }

  private stopTypewriter(): void {
    if (this.typewriterTimer !== null) {
      clearInterval(this.typewriterTimer);
      this.typewriterTimer = null;
    }
  }

  /** Efecto 3D de inclinación en el diálogo según posición del ratón. */
  private setupDialogue3DTilt(): void {
    // Solo en dispositivos con hover (no táctil)
    if (!window.matchMedia('(hover: hover)').matches) return;

    const handler = (ev: MouseEvent) => {
      const rect = this.dialogueEl.getBoundingClientRect();
      const centerX = rect.left + rect.width / 2;
      const centerY = rect.top + rect.height / 2;
      // Calcular rotación basada en posición relativa al centro (max +/- 2deg)
      const rx = ((ev.clientY - centerY) / rect.height) * -2;
      const ry = ((ev.clientX - centerX) / rect.width) * 2;
      this.dialogueEl.style.transform = `rotateX(${rx}deg) rotateY(${ry}deg)`;
    };

    const reset = () => {
      this.dialogueEl.style.transform = '';
    };

    this.dialogueEl.addEventListener('pointermove', handler);
    this.dialogueEl.addEventListener('pointerleave', reset);
  }

  private buildDialogueNode(view: DialogueView): El<'div'>[] {
    const engine = this.opts.engine;
    const portrait = el('div', { className: 'ae-portrait' });
    const img = view.portrait ? engine.game.assets.images?.[view.portrait] : undefined;
    if (img) {
      const src = img.src.startsWith('data:') ? img.src : (this.opts.assetBaseUrl ?? '') + img.src;
      if (img.kind === 'spritesheet' && img.frameWidth && img.frameHeight) {
        const frame = el('div', {
          className: 'ae-portrait-frame',
          attrs: { role: 'img', 'aria-label': `Retrato de ${view.speakerName}`, style: `background-image:url('${src}')` }
        });
        const probe = new Image();
        probe.onload = () => {
          const cols = Math.max(1, Math.round(probe.naturalWidth / img.frameWidth!));
          const rows = Math.max(1, Math.round(probe.naturalHeight / img.frameHeight!));
          frame.style.backgroundSize = `${cols * 100}% ${rows * 100}%`;
          frame.style.backgroundPosition = '0% 0%';
        };
        probe.src = src;
        portrait.append(frame);
      } else {
        portrait.append(el('img', { src, alt: `Retrato de ${view.speakerName}` }));
      }
    }
    const body = el('div', { className: 'ae-dialogue-body' });
    body.append(el('div', { className: 'ae-speaker', textContent: view.speakerName }));
    const textWrap = el('div', { className: 'ae-dialogue-text', attrs: { 'aria-live': 'polite' } });
    textWrap.append(el('span', { className: 'ae-typewriter-cursor' }));
    body.append(textWrap);

    if (view.choices.length > 0) {
      const choices = el('div', { className: 'ae-choices', attrs: { role: 'group', 'aria-label': 'Respuestas disponibles' } });
      for (const c of view.choices) {
        choices.append(button(c.text, () => engine.chooseOption(c.id), { className: 'ae-choice', disabled: !c.enabled }));
      }
      body.append(choices);
    } else {
      const actions = el('div', { className: 'ae-dialogue-actions' });
      actions.append(button(view.isEnd ? 'Cerrar' : 'Continuar ▸', () => engine.advanceDialogue(), { className: 'ae-btn primary' }));
      if (!view.isEnd) actions.append(button('Cerrar conversación', () => engine.closeDialogue()));
      body.append(actions);
    }
    return [portrait, body];
  }

  /* --------------------------- cajón lateral --------------------------- */

  private toggleDrawer(tab: DrawerTab): void {
    if (this.drawerOpen && this.drawerTab === tab) {
      this.closeDrawer();
      return;
    }
    this.drawerTab = tab;
    this.drawerOpen = true;
    this.drawerEl.classList.add('open');
    this.renderDrawer();
    this.drawerBody.querySelector<HTMLElement>('button')?.focus();
  }

  private closeDrawer(): void {
    this.drawerOpen = false;
    this.drawerEl.classList.remove('open');
  }

  private renderDrawer(): void {
    clear(this.drawerBody);
    const tabs = el('div', { className: 'ae-tabs', attrs: { role: 'tablist' } });
    const tabDefs: Array<[DrawerTab, string]> = [
      ['objectives', 'Objetivos'],
      ['inventory', 'Inventario'],
      ['diary', 'Diario'],
      ['map', 'Mapa']
    ];
    if (this.hasScoreboard) tabDefs.push(['puntaje', 'Marcador']);
    for (const [id, label] of tabDefs) {
      const b = button(label, () => {
        this.drawerTab = id;
        this.renderDrawer();
      });
      b.setAttribute('aria-pressed', String(this.drawerTab === id));
      tabs.append(b);
    }
    this.drawerBody.append(tabs);
    if (this.drawerTab === 'objectives') this.renderObjectives();
    else if (this.drawerTab === 'inventory') this.renderInventory();
    else if (this.drawerTab === 'diary') this.renderDiary();
    else if (this.drawerTab === 'puntaje') this.renderScore();
    else this.renderMap();
  }

  /**
   * Marcador educativo: separado del progreso de la aventura. Sólo cuenta los
   * ejercicios evaluables (con `puntos`), usando siempre el mejor intento.
   * Las pistas y la exploración no suman ni restan aquí.
   */
  private renderScore(): void {
    const engine = this.opts.engine;
    const score = engine.getExerciseScore();
    const total = el('div', { className: 'ae-card ae-score-total' });
    total.append(
      el('h4', { textContent: 'Resultado educativo' }),
      el('p', {
        textContent: score.maximo > 0 && score.porcentaje !== null
          ? `${score.obtenido} de ${score.maximo} puntos (${score.porcentaje}%)`
          : 'Sin calificación'
      })
    );
    this.drawerBody.append(total);

    const juegos = engine.game.minigames ?? {};
    for (const [id, record] of Object.entries(score.porEjercicio)) {
      const spec = juegos[id];
      const card = el('div', { className: 'ae-card' });
      card.append(el('h4', { textContent: spec?.titulo ?? id }));
      if (record.attempts === 0) {
        card.append(el('p', { textContent: `Pendiente · vale ${record.max} puntos` }));
      } else {
        card.append(el('p', {
          textContent: `Mejor: ${record.best}/${record.max} · Último: ${record.last} · Primero: ${record.first} · Intentos: ${record.attempts}`
        }));
      }
      this.drawerBody.append(card);
    }
    for (const [id, spec] of Object.entries(juegos)) {
      if ((spec.puntos ?? 0) > 0 || score.porEjercicio[id]) continue;
      const card = el('div', { className: 'ae-card' });
      card.append(el('h4', { textContent: spec.titulo ?? id }));
      card.append(el('p', { textContent: 'Actividad realizada (no evaluable, no cuenta para el puntaje).' }));
      this.drawerBody.append(card);
    }
  }

  private renderObjectives(): void {
    for (const o of this.opts.engine.getObjectives()) {
      const card = el('div', { className: `ae-card ae-objective-item ${o.completed ? 'completed' : ''}` });
      card.append(el('h4', { textContent: `${o.completed ? '✔ ' : o.active ? '▶ ' : ''}${o.title}` }));
      card.append(el('p', { textContent: o.description }));
      if (o.active && o.hints.length > 0) {
        const revealed = this.revealedHints[o.id] ?? 0;
        const list = el('div', { className: 'ae-hint-list' });
        for (let i = 0; i < revealed && i < o.hints.length; i++) {
          list.append(el('div', { className: 'ae-hint', textContent: `💡 ${o.hints[i]}` }));
        }
        if (revealed < o.hints.length) {
          list.append(button(`Ver pista ${revealed + 1} de ${o.hints.length}`, () => this.showNextHint()));
        }
        card.append(list);
      }
      this.drawerBody.append(card);
    }
  }

  private renderInventory(): void {
    const items = this.opts.engine.getInventory();
    if (items.length === 0) {
      this.drawerBody.append(el('p', { textContent: 'Tu inventario está vacío.' }));
      return;
    }
    for (const it of items) {
      this.drawerBody.append(
        el('div', { className: 'ae-card' }, [el('h4', { textContent: it.name }), el('p', { textContent: it.description })])
      );
    }
  }

  private renderDiary(): void {
    const ev = this.opts.engine.getDiary();
    if (ev.length === 0) {
      this.drawerBody.append(el('p', { textContent: 'Aún no has registrado evidencias.' }));
      return;
    }
    for (const e of ev) this.drawerBody.append(this.evidenceCard(e));
  }

  private evidenceCard(e: Evidence): El<'div'> {
    const card = el('div', { className: 'ae-card' });
    card.append(el('span', { className: 'ae-badge', textContent: kindLabel(e.kind) }));
    card.append(el('h4', { textContent: e.name }));
    card.append(el('p', { textContent: e.description }));
    card.append(el('p', { textContent: `Origen: ${e.origin}` }));
    if (e.fictionalNotice) card.append(el('div', { className: 'ae-notice', textContent: e.fictionalNotice }));
    return card;
  }

  private renderMap(): void {
    for (const s of this.opts.engine.getMap()) {
      const card = el('div', { className: 'ae-card ae-map-item' });
      const label = el('div', {}, [
        el('span', { className: s.visited ? 'visited' : 'unvisited', textContent: `${s.current ? '📍 ' : ''}${s.name}` }),
        el('span', { textContent: s.visited ? '' : ' (no visitado)' })
      ]);
      if (s.exits.length > 0) label.append(el('p', { textContent: `Conexiones: ${s.exits.map((x) => x.label).join(', ')}` }));
      card.append(label);
      if (!s.current) {
        const reachable = s.visited || this.opts.engine.getVisibleExits().some((e) => e.to === s.id);
        card.append(button('Ir', () => this.opts.engine.travelTo(s.id), { disabled: !reachable }));
      }
      this.drawerBody.append(card);
    }
  }

  /* --------------------------- pistas --------------------------- */

  private showNextHint(): void {
    const active = this.opts.engine.getActiveObjective();
    if (!active || active.hints.length === 0) {
      this.toast('No hay pistas disponibles para este objetivo.', 'hint');
    } else {
      const revealed = this.revealedHints[active.id] ?? 0;
      if (revealed < active.hints.length) {
        this.revealedHints[active.id] = revealed + 1;
        this.toast(active.hints[revealed], 'hint');
      } else {
        this.toast('Ya has visto todas las pistas de este objetivo.', 'hint');
      }
    }
    this.drawerTab = 'objectives';
    if (!this.drawerOpen) {
      this.drawerOpen = true;
      this.drawerEl.classList.add('open');
    }
    this.renderDrawer();
  }

  /* --------------------------- controles --------------------------- */

  private toggleSound(btn: HTMLButtonElement): void {
    this.sound.unlock();
    const next = !this.sound.isEnabled;
    this.sound.setEnabled(next);
    btn.textContent = next ? '🔊 Sonido' : '🔇 Sonido';
    btn.setAttribute('aria-pressed', String(next));
  }

  private toggleMusic(btn: HTMLButtonElement): void {
    this.musicOn = !this.musicOn;
    localStorage.setItem('ae-music', this.musicOn ? 'on' : 'off');
    this.music.setEnabled(this.musicOn);
    btn.textContent = this.musicOn ? '🎵 Música' : '🔇 Música';
    btn.setAttribute('aria-pressed', String(this.musicOn));
  }

  private toggleFullscreen(): void {
    if (!document.fullscreenElement) void this.root.requestFullscreen?.().catch(() => undefined);
    else void document.exitFullscreen?.().catch(() => undefined);
  }

  private confirmRestart(): void {
    this.openConfirm('Reiniciar partida', 'Comenzarás desde el principio y se borrará el progreso actual. ¿Continuar?', () => {
      this.closeModal();
      this.opts.engine.newGame();
    });
  }

  /* --------------------------- toasts --------------------------- */
  private toast(text: string, tone: 'info' | 'success' | 'warning' | 'hint' = 'info'): void {
    const t = el('div', { className: `ae-toast ${tone}` });
    t.append(el('div', { className: 'ae-toast-text', textContent: text }));
    const closeBtn = button('✓', () => t.remove(), { className: 'ae-toast-close', ariaLabel: 'Cerrar' });
    t.append(closeBtn);
    this.toastsEl.append(t);
    // Máximo 3 avisos acumulados: al llegar uno nuevo se descarta el más antiguo.
    while (this.toastsEl.children.length > 3) this.toastsEl.firstElementChild?.remove();
    // Auto-focus el botón para que se pueda cerrar con Enter
    closeBtn.focus();
  }

  /** Suma puntos del mundo (p. ej. neutralizar un enemigo) y actualiza el HUD. */
  private addWorldScore(points: number, reason: string): void {
    this.worldScore += points;
    this.worldScoreEl.style.display = '';
    this.worldScoreEl.textContent = `★ ${this.worldScore}`;
    this.worldScoreEl.classList.remove('ae-worldscore-pop');
    void this.worldScoreEl.offsetWidth; // reinicia la animación
    this.worldScoreEl.classList.add('ae-worldscore-pop');
    this.toast(`+${points} · ${reason}`, 'success');
  }

  /* --------------------------- modales --------------------------- */

  private closeModal(): void {
    clear(this.modalHost);
    this.modalHost.style.pointerEvents = 'none';
  }

  private mountModal(node: HTMLElement): void {
    clear(this.modalHost);
    this.modalHost.style.pointerEvents = 'auto';
    this.modalHost.append(el('div', { className: 'ae-modal-backdrop' }, [node]));
    node.querySelector<HTMLElement>('button, input, select')?.focus();
  }

  private openConfirm(title: string, message: string, onYes: () => void): void {
    const modal = el('div', { className: 'ae-modal', attrs: { role: 'dialog', 'aria-modal': 'true' } });
    modal.append(el('h1', { textContent: title }), el('p', { textContent: message }));
    const row = el('div', { className: 'ae-row' });
    row.append(button('Sí, continuar', onYes, { className: 'ae-btn danger' }));
    row.append(button('Cancelar', () => this.closeModal()));
    modal.append(row);
    this.mountModal(modal);
  }

  /* --------------------- mecánica de conclusión --------------------- */

  private openConclusion(): void {
    const config = this.opts.engine.getConclusionConfig();
    if (!config) return;
    const prior = this.opts.engine.getConclusionRecord();
    this.selection = {
      hypothesis: prior?.hypothesis ?? '',
      evidence: prior ? [...prior.evidence] : [],
      measures: prior ? [...prior.measures] : []
    };

    const modal = el('div', { className: 'ae-modal', attrs: { role: 'dialog', 'aria-modal': 'true' } });
    modal.append(el('h1', { textContent: config.title }));
    modal.append(el('p', { className: 'lead', textContent: config.instructions }));
    const errors = el('div', { className: 'ae-errors', attrs: { style: 'display:none' } });
    modal.append(errors);

    modal.append(el('h2', { textContent: '1. Elige una hipótesis' }));
    const hypoWrap = el('div');
    for (const h of config.hypotheses) {
      hypoWrap.append(this.radioOption('hyp', h.id, h.label, h.description, this.selection.hypothesis === h.id, () => {
        this.selection.hypothesis = h.id;
      }));
    }
    modal.append(hypoWrap);

    modal.append(el('h2', { textContent: '2. Selecciona las evidencias de tu diario que la apoyan' }));
    const diary = this.opts.engine.getDiary();
    if (diary.length === 0) {
      modal.append(el('p', { textContent: 'No has registrado ninguna evidencia todavía. Vuelve a investigar antes de concluir.' }));
    }
    const evWrap = el('div');
    for (const e of diary) {
      evWrap.append(this.checkOption(e.id, `${e.name} (${kindLabel(e.kind)})`, e.description, this.selection.evidence.includes(e.id), (checked) => {
        this.toggleInList(this.selection.evidence, e.id, checked);
      }));
    }
    modal.append(evWrap);

    modal.append(el('h2', { textContent: '3. Elige medidas de actuación' }));
    const measWrap = el('div');
    for (const m of config.measures) {
      measWrap.append(this.checkOption(m.id, m.label, m.description, this.selection.measures.includes(m.id), (checked) => {
        this.toggleInList(this.selection.measures, m.id, checked);
      }));
    }
    modal.append(measWrap);

    const row = el('div', { className: 'ae-row' });
    row.append(button('Presentar conclusión', () => {
      const result = this.opts.engine.submitConclusion({ ...this.selection });
      if (!result.ok) {
        errors.style.display = 'block';
        clear(errors);
        errors.append(el('strong', { textContent: 'Revisa tu argumentación:' }));
        const ul = el('ul');
        for (const err of result.errors) ul.append(el('li', { textContent: err }));
        errors.append(ul);
        return;
      }
      // El motor emite gameCompleted → showEnding reemplaza este modal.
    }, { className: 'ae-btn primary' }));
    row.append(button('Seguir investigando', () => this.closeModal()));
    modal.append(row);

    this.mountModal(modal);
  }

  private radioOption(name: string, value: string, title: string, desc: string, checked: boolean, onChange: () => void): El<'label'> {
    const input = el('input', { type: 'radio', name, value, checked });
    input.addEventListener('change', () => {
      if (input.checked) onChange();
    });
    const label = el('label', { className: `ae-opt ${checked ? 'selected' : ''}` }, [
      input,
      el('div', {}, [el('div', { className: 't', textContent: title }), el('div', { className: 'd', textContent: desc })])
    ]);
    return label;
  }

  private checkOption(value: string, title: string, desc: string, checked: boolean, onChange: (checked: boolean) => void): El<'label'> {
    const input = el('input', { type: 'checkbox', value, checked });
    input.addEventListener('change', () => onChange(input.checked));
    return el('label', { className: `ae-opt ${checked ? 'selected' : ''}` }, [
      input,
      el('div', {}, [el('div', { className: 't', textContent: title }), el('div', { className: 'd', textContent: desc })])
    ]);
  }

  private toggleInList(list: string[], value: string, add: boolean): void {
    const idx = list.indexOf(value);
    if (add && idx < 0) list.push(value);
    if (!add && idx >= 0) list.splice(idx, 1);
  }

  /* --------------------------- desenlace --------------------------- */

  private showEnding(endingId: string): void {
    const ending = this.opts.engine.game.endings[endingId];
    const result = this.opts.engine.getResult();
    if (!ending || !result) return;

    const modal = el('div', { className: 'ae-modal', attrs: { role: 'dialog', 'aria-modal': 'true' } });
    modal.append(el('span', { className: `ae-quality ${ending.quality}`, textContent: qualityLabel(ending.quality) }));
    modal.append(el('h1', { textContent: ending.title }));
    modal.append(el('p', { className: 'lead', textContent: ending.summary }));

    const fb = el('div', { className: 'ae-fb' });
    fb.append(this.feedbackBlock('Qué estuvo bien', ending.feedback.wellDone, 'good'));
    fb.append(this.feedbackBlock('Qué faltó', ending.feedback.missing, 'missing'));
    fb.append(this.feedbackBlock('Cómo mejorar', ending.feedback.improve, 'improve'));
    modal.append(fb);

    modal.append(el('h2', { textContent: 'Resumen educativo' }));
    const summary = el('div', { className: 'ae-card' });
    summary.append(
      el('p', { textContent: `Juego: ${result.gameId} (v${result.gameVersion}) · Instancia: ${result.instanceId}` }),
      el('p', { textContent: `Desenlace: ${result.endingTitle}` }),
      el('p', { textContent: result.educationalSummary })
    );
    if (result.hypothesis) {
      const hyp = this.opts.engine.game.conclusion?.hypotheses.find((h) => h.id === result.hypothesis);
      summary.append(el('p', { textContent: `Hipótesis presentada: ${hyp?.label ?? result.hypothesis}` }));
    }
    if (result.measures && result.measures.length > 0) {
      const labels = result.measures.map((m) => this.opts.engine.game.conclusion?.measures.find((x) => x.id === m)?.label ?? m);
      summary.append(el('p', { textContent: `Medidas propuestas: ${labels.join(', ')}` }));
    }
    summary.append(el('p', { textContent: `Evidencias reunidas: ${result.evidenceCollected.length} · Objetivos: ${result.objectivesCompleted.length}` }));
    if (result.exerciseScore && result.exerciseScore.maximo > 0) {
      const pct = result.exerciseScore.porcentaje;
      summary.append(el('p', {
        textContent: `Resultado educativo: ${result.exerciseScore.obtenido} de ${result.exerciseScore.maximo} puntos${pct !== null ? ` (${pct}%)` : ''}`
      }));
    } else {
      summary.append(el('p', { textContent: 'Resultado educativo: sin calificación' }));
    }
    modal.append(summary);

    const row = el('div', { className: 'ae-row' });
    if (ending.allowContinue) {
      row.append(button('Seguir investigando', () => {
        this.closeModal();
        this.opts.engine.continueAfterEnding();
      }, { className: 'ae-btn primary' }));
    }
    row.append(button('Empezar de nuevo', () => {
      this.closeModal();
      this.opts.engine.newGame();
    }));
    modal.append(row);

    this.mountModal(modal);
  }

  private feedbackBlock(title: string, items: string[], cls: string): El<'div'> {
    const block = el('div', { className: `ae-fb-block ${cls}` });
    block.append(el('h3', { textContent: title }));
    const ul = el('ul', { className: 'ae-list' });
    for (const it of items) ul.append(el('li', { textContent: it }));
    block.append(ul);
    return block;
  }

  private onMapEnter(mapId: string): void {
    this.opts.engine.enterMap(mapId);
    const mapDef = this.opts.engine.game.maps?.[mapId];
    if (mapDef?.description) {
      this.sceneDescEl.textContent = mapDef.description;
    }
  }

  destroy(): void {
    for (const d of this.disposers) d();
    this.disposers.length = 0;
    this.sound.destroy();
    this.music.destroy();
    this.sfx.destroy();
    this.procedural.destroy();
    this.phaser?.destroy();
    this.view?.destroy();
    this.battle?.close();
    this.minigame?.destroy();
    this.minigame = undefined;
    this.content?.destroy();
    this.content = undefined;
    this.video?.destroy();
    this.video = undefined;
    this.root.remove();
  }
}

function kindLabel(kind: Evidence['kind']): string {
  switch (kind) {
    case 'observacion':
      return 'Observación';
    case 'testimonio':
      return 'Testimonio';
    case 'documento':
      return 'Documento';
    case 'resultado_simulado':
      return 'Resultado simulado (ficticio)';
    default:
      return 'Evidencia';
  }
}

function qualityLabel(q: 'insuficiente' | 'parcial' | 'solido'): string {
  return q === 'solido' ? 'Propuesta bien fundamentada' : q === 'parcial' ? 'Propuesta parcialmente fundamentada' : 'Acusación precipitada';
}
